import { trpc } from "../api/trpc";
import { analyticsService } from "../services/analytics/analyticsService";

export interface UseSavedBooksParams {
  page?: number;
  limit?: number;
  sort?: "savedAt_desc" | "savedAt_asc" | "title_asc" | "title_desc";
}

/**
 * Hook for fetching paginated saved books (user's library)
 */
export const useSavedBooks = (params: UseSavedBooksParams = {}) => {
  const { page = 1, limit = 12, sort = "savedAt_desc" } = params;

  return trpc.savedBooks.getSavedBooks.useQuery(
    { page, limit, sort },
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

/**
 * Hook for checking if a specific book is saved
 */
export const useIsBookSaved = (bookId: string) => {
  return trpc.savedBooks.isBookSaved.useQuery(
    { bookId },
    {
      enabled: !!bookId,
      staleTime: 30 * 1000, // 30 seconds
      cacheTime: 60 * 1000, // 1 minute
    },
  );
};

/**
 * Hook for getting saved books count (for navigation badge)
 */
export const useSavedBooksCount = (authenticated: boolean) => {
  return trpc.savedBooks.getSavedBooksCount.useQuery(undefined, {
    enabled: authenticated,
    staleTime: 60 * 1000, // 1 minute
    cacheTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook for saving a book to library
 */
export const useSaveBook = () => {
  const utils = trpc.useUtils();

  return trpc.savedBooks.saveBook.useMutation({
    onSuccess: (_data, variables) => {
      // Invalidate all saved books queries to refetch
      utils.savedBooks.getSavedBooks.invalidate();
      utils.savedBooks.getSavedBooksCount.invalidate();
      utils.savedBooks.isBookSaved.invalidate();

      // Track analytics
      analyticsService.track("book.saved", { book_id: variables.bookId });
    },
  });
};

/**
 * Hook for removing a book from library
 */
export const useUnsaveBook = () => {
  const utils = trpc.useUtils();

  return trpc.savedBooks.unsaveBook.useMutation({
    onSuccess: (_data, variables) => {
      // Invalidate all saved books queries to refetch
      utils.savedBooks.getSavedBooks.invalidate();
      utils.savedBooks.getSavedBooksCount.invalidate();
      utils.savedBooks.isBookSaved.invalidate();

      // Track analytics
      analyticsService.track("book.unsaved", { book_id: variables.bookId });
    },
  });
};
